/**
 * `SCR-SIGNIN` (10 states) and `SCR-RESET` (4 states). FLOWS_SPEC §3.1, §3.2.
 * OWNER: web-frontend-engineer.
 *
 * One centred column: house name, two fields, one action, one human route out.
 * NOTHING ELSE — no hero, no value proposition, no press logos, no badges, no
 * app-store links, no sign-up, no social login, no consent overlay. It collects
 * exactly an identifier and a credential, ever.
 *
 * `ST-SIGNIN-REFUSED` is IDENTICAL in copy, status and timing for a wrong
 * credential and an unknown address ([T-14]) — which is a server property this
 * screen must not undo by branching on the code. It does not: there is one
 * refusal branch and it renders one string.
 */

import { useEffect, useRef, useState } from 'react';
import { t } from '@pt/copy';
import { ApiRejection, ApiUnreachable, api } from '../lib/api.ts';
import { useNavigation } from '../app/Router.tsx';
import { useSession } from '../app/SessionProvider.tsx';
import { AppShell } from '../app/AppShell.tsx';
import { Button, ErrorStatement, Link, Statement, TextInput } from '../components/primitives.tsx';
import { OfflineStatement } from '../components/states.tsx';

type SignInState =
  | 'default'
  | 'incomplete'
  | 'busy'
  | 'refused'
  | 'ratelimited'
  | 'offline'
  | 'serverdown';

export function SignInScreen() {
  const { navigate, match } = useNavigation();
  const { signIn, expired } = useSession();
  const [identifier, setIdentifier] = useState('');
  const [credential, setCredential] = useState('');
  const [state, setState] = useState<SignInState>('default');
  const identifierRef = useRef<HTMLInputElement>(null);

  const arrivedSignedOut = match.query.get('state') === 'signedout';

  // This screen has one job, so the identifier takes focus on load.
  useEffect(() => {
    const field = document.getElementById('pt-signin-identifier');
    if (field instanceof HTMLInputElement) field.focus();
  }, []);

  async function submit() {
    if (identifier.trim() === '' || credential === '') {
      setState('incomplete');
      const first = identifier.trim() === '' ? 'pt-signin-identifier' : 'pt-signin-credential';
      document.getElementById(first)?.focus();
      return;
    }
    setState('busy');
    try {
      const session = await api.signIn({ identifier, credential });
      signIn(session);
      navigate(session.landing, { replace: true });
    } catch (error) {
      if (error instanceof ApiUnreachable) {
        setState('offline');
      } else if (error instanceof ApiRejection) {
        setState(error.status === 429 ? 'ratelimited' : error.status >= 500 ? 'serverdown' : 'refused');
      } else {
        setState('serverdown');
      }
      identifierRef.current?.focus();
      document.getElementById('pt-signin-identifier')?.focus();
    }
  }

  return (
    <AppShell title={t('signin.default.heading')} width="login" chrome={false}>
      {state === 'offline' ? <OfflineStatement /> : null}
      {expired ? <Statement>{t('signin.expired.message')}</Statement> : null}
      {arrivedSignedOut ? <Statement>{t('signin.signedout.message')}</Statement> : null}

      {state === 'refused' ? (
        <ErrorStatement what={t('signin.refused.message')} assertive />
      ) : null}
      {state === 'ratelimited' ? (
        <ErrorStatement what={t('signin.ratelimited.message')} assertive />
      ) : null}
      {state === 'serverdown' ? (
        <ErrorStatement what={t('signin.serverdown.message')} assertive />
      ) : null}
      {state === 'offline' ? <ErrorStatement what={t('signin.offline.message')} /> : null}

      <TextInput
        id="pt-signin-identifier"
        label={t('signin.field.identifier.label')}
        value={identifier}
        type="email"
        autoComplete="username"
        onChange={(value) => {
          setIdentifier(value);
          if (state === 'incomplete') setState('default');
        }}
        onEnter={() => {
          void submit();
        }}
        {...(state === 'incomplete' && identifier.trim() === ''
          ? { error: t('signin.incomplete.identifier') }
          : {})}
      />
      <TextInput
        id="pt-signin-credential"
        label={t('signin.field.credential.label')}
        value={credential}
        type="password"
        autoComplete="current-password"
        onChange={(value) => {
          setCredential(value);
          if (state === 'incomplete') setState('default');
        }}
        onEnter={() => {
          void submit();
        }}
        {...(state === 'incomplete' && credential === ''
          ? { error: t('signin.incomplete.credential') }
          : {})}
      />

      <div className="pt-actions">
        <Button
          variant="primary"
          busy={state === 'busy'}
          busyLabel={t('signin.busy.action')}
          onClick={() => {
            void submit();
          }}
        >
          {t('signin.default.action')}
        </Button>
      </div>

      <p className="pt-block">
        <Link href="/reset" onNavigate={navigate}>
          {t('signin.default.reset')}
        </Link>
      </p>
    </AppShell>
  );
}

export function ResetScreen() {
  const { navigate } = useNavigation();
  const [identifier, setIdentifier] = useState('');
  const [state, setState] = useState<'default' | 'busy' | 'sent' | 'offline'>('default');

  return (
    <AppShell title={t('reset.default.heading')} width="login" chrome={false}>
      {state === 'offline' ? <OfflineStatement /> : null}
      {/* Identical copy whether or not the address belongs to a client ([T-14]). */}
      {state === 'sent' ? <Statement>{t('reset.sent.message')}</Statement> : null}
      {state === 'sent' ? null : (
        <>
          <TextInput
            label={t('reset.default.field.label')}
            value={identifier}
            type="email"
            autoComplete="username"
            onChange={setIdentifier}
          />
          <div className="pt-actions">
            <Button
              variant="primary"
              busy={state === 'busy'}
              busyLabel={t('reset.busy.action')}
              onClick={() => {
                setState('busy');
                api
                  .requestPasswordReset(identifier)
                  .then(() => setState('sent'))
                  .catch((error: unknown) =>
                    setState(error instanceof ApiUnreachable ? 'offline' : 'sent'),
                  );
              }}
            >
              {t('reset.default.action')}
            </Button>
          </div>
        </>
      )}
      <p className="pt-block">
        <Link href="/signin" onNavigate={navigate}>
          {t('reset.default.back')}
        </Link>
      </p>
    </AppShell>
  );
}
